import { Alert, Anchor, Button, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useConfirmPasswordReset, useRequestPasswordReset, useVerifyEmail } from '@/api/account';
import { AuthLayout } from './AuthLayout';

/** „Passwort vergessen“: Link per E-Mail anfordern (SEC-04). */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const request = useRequestPasswordReset();
  const [email, setEmail] = useState('');
  return (
    <AuthLayout>
      <Stack>
        <Title order={3} ta="center">
          {t('passwordReset.title')}
        </Title>
        {request.isSuccess ? (
          <Alert color="green" icon={<IconCircleCheck size={18} />}>
            {t('passwordReset.sent')}
          </Alert>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              request.mutate(email);
            }}
          >
            <Stack>
              <Text size="sm" c="dimmed">
                {t('passwordReset.hint')}
              </Text>
              <TextInput label={t('login.email')} type="email" required value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
              <Button type="submit" loading={request.isPending}>
                {t('passwordReset.send')}
              </Button>
            </Stack>
          </form>
        )}
        <Anchor component={Link} to="/login" size="sm" ta="center">
          {t('passwordReset.backToLogin')}
        </Anchor>
      </Stack>
    </AuthLayout>
  );
}

/** Neues Passwort mit Token aus der E-Mail setzen. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirmPasswordReset();
  const [password, setPassword] = useState('');
  const token = params.get('token') ?? '';
  return (
    <AuthLayout>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirm.mutate({ token, newPassword: password });
        }}
      >
        <Stack>
          <Title order={3} ta="center">
            {t('passwordReset.newTitle')}
          </Title>
          {confirm.isSuccess ? (
            <>
              <Alert color="green" icon={<IconCircleCheck size={18} />}>
                {t('passwordReset.done')}
              </Alert>
              <Button onClick={() => navigate('/login')}>{t('login.submit')}</Button>
            </>
          ) : (
            <>
              {confirm.isError && (
                <Alert color="red" icon={<IconAlertCircle size={18} />}>
                  {t('passwordReset.invalid')}
                </Alert>
              )}
              <PasswordInput
                label={t('profile.newPassword')}
                description={t('register.passwordHint')}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <Button type="submit" disabled={password.length < 8 || !token} loading={confirm.isPending}>
                {t('common.save')}
              </Button>
            </>
          )}
        </Stack>
      </form>
    </AuthLayout>
  );
}

/** Bestätigung der E-Mail-Adresse über den Link aus der E-Mail. */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const verify = useVerifyEmail();
  const started = useRef(false);
  const token = params.get('token') ?? '';
  useEffect(() => {
    if (!started.current && token) {
      started.current = true;
      verify.mutate(token);
    }
  }, [token, verify]);
  return (
    <AuthLayout>
      <Stack>
        <Title order={3} ta="center">
          {t('verifyEmail.title')}
        </Title>
        {verify.isSuccess && (
          <Alert color="green" icon={<IconCircleCheck size={18} />}>
            {t('verifyEmail.done')}
          </Alert>
        )}
        {(verify.isError || !token) && (
          <Alert color="red" icon={<IconAlertCircle size={18} />}>
            {t('verifyEmail.invalid')}
          </Alert>
        )}
        {verify.isPending && <Text ta="center">…</Text>}
        <Anchor component={Link} to="/tracker" ta="center" size="sm">
          {t('notFound.back')}
        </Anchor>
      </Stack>
    </AuthLayout>
  );
}
